import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TriangleAlert } from 'lucide-react';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import useUpdateCostCategory from '@/features/dashboard/costCategories/hooks/useUpdateCostCategory.ts';
import {
  costCategorySchema,
  CostCategoryFormData,
  CostCategoryInputData,
} from '@/features/dashboard/costCategories/schemas/costCategorySchema.ts';
import CostCategoryForm from '@/features/dashboard/costCategories/components/CostCategoryForm.tsx';
import { CostCategoryDto } from '@/features/dashboard/costCategories/types/CostCategoryDto.ts';
import { getCostCategoryNameTakenMessage } from '@/features/dashboard/costCategories/api/costCategoryErrors.ts';

interface EditCostCategoryModalProps {
  costCategory: CostCategoryDto;
  onClose: () => void;
}

const EditCostCategoryModal = ({
  costCategory,
  onClose,
}: EditCostCategoryModalProps) => {
  const updateMutation = useUpdateCostCategory(costCategory.id);

  const methods = useForm<CostCategoryInputData>({
    resolver: zodResolver(costCategorySchema),
    values: { name: costCategory.name, color: costCategory.color },
  });

  const { handleSubmit, setError } = methods;

  const onSubmit = (data: CostCategoryFormData) => {
    updateMutation.mutate(data, {
      onSuccess: () => onClose(),
      onError: (error) => {
        const message = getCostCategoryNameTakenMessage(error);
        if (message) setError('name', { message });
      },
    });
  };

  return (
    <Modal onClose={onClose} className="max-w-[520px]">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader title="Edytuj kategorię kosztów" onClose={onClose} />
          <ModalContent>
            <div className="flex flex-row items-start gap-2.5 rounded-[10px] bg-accent-100 p-3">
              <TriangleAlert
                className="mt-px size-4 shrink-0 text-accent-900"
                aria-hidden
              />
              <p className="text-[13px] leading-[18px] text-accent-900">
                Zmiana nazwy przemianuje tę kategorię także w dotychczasowych
                wydatkach.
              </p>
            </div>
            <CostCategoryForm />
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={updateMutation.isPending}
            >
              Zapisz zmiany
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditCostCategoryModal;
